import { useEffect, useState } from 'react'
import { Loader2, Paperclip, Save } from 'lucide-react'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { apiService } from '@/services/api'
import { s3Service } from '@/services/s3Service'
import { mensajeDeError } from '@/shared/lib/mensajeDeError'
import {
  ETAPAS_DE_ALTA,
  erroresDelAlta,
  type CamposDelAlta,
} from '@/features/rh/lib/altaManualDeCandidato'
import { origenesDeCandidato } from '@/features/rh/api/origenesDeCandidato'
import { CampoDeEvaluacion } from '@/features/rh/components/CampoDeEvaluacion'
import { SelectorDeResidencia } from '@/features/rh/components/SelectorDeResidencia'
import { AVISO_DE_ALTA_SIN_EVALUACION } from '@/features/rh/lib/evaluacionPendiente'
import {
  pideQuienRecomendo,
  type OrigenDeCandidato,
} from '@/features/rh/lib/origenDelCandidato'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface VacanteMinima {
  id: number
  puestoSolicitar?: string
  folio?: string
}

interface Props {
  vacantes: VacanteMinima[]
  /** Vacante preseleccionada cuando el alta sale del detalle de una. */
  vacanteInicial?: number
  onCancelar: () => void
  onRegistrado: () => void
}

const VACIO: CamposDelAlta = {
  sourceId: '',
  referredByText: '',
  jobRequestId: '',
  status: 'pendiente',
  fullName: '',
  age: '',
  profesion: '',
  phone: '',
  email: '',
  experienceYears: '',
  experienceDescription: '',
  certificaciones: '',
  residenceCity: '',
  salaryExpectation: '',
  cvUrl: '',
  evaluationUrl: '',
}

/**
 * Etiqueta de campo, con el asterisco o el «(opcional)» según toque.
 *
 * Va fuera del componente a propósito: definida adentro, React la trataría
 * como un tipo distinto en cada render y volvería a montar el subárbol en
 * cada tecla.
 */
const Etiqueta = ({
  htmlFor,
  children,
  obligatorio,
  nota,
}: {
  htmlFor: string
  children: React.ReactNode
  obligatorio?: boolean
  /** Aclaración junto al asterisco, para lo obligatorio que no bloquea el envío. */
  nota?: string
}) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1.5">
    {children}{' '}
    {obligatorio ? (
      <>
        <span className="text-red-500">*</span>
        {nota && <span className="text-gray-400 font-normal"> ({nota})</span>}
      </>
    ) : (
      <span className="text-gray-400 font-normal">(opcional)</span>
    )}
  </label>
)

/** Caja blanca con encabezado, igual que las del alta de herramientas. */
const Seccion = ({
  titulo,
  children,
}: {
  titulo: string
  children: React.ReactNode
}) => (
  <section className="bg-white rounded-xl border border-[#E0E0E0] shadow-sm">
    <header className="px-6 py-4 border-b border-[#E0E0E0]">
      <h2 className="text-base font-semibold text-gray-800">{titulo}</h2>
    </header>
    <div className="p-6 space-y-5">{children}</div>
  </section>
)

/**
 * Alta de un candidato a mano, sin el formulario público (RH-05).
 *
 * Pide cuatro datos y deja el correo opcional, a diferencia del formulario
 * público, que además pide experiencia y consentimiento de privacidad. Es para
 * cuando el candidato llegó por otra vía y no tiene caso mandarle un enlace.
 *
 * El aviso del correo no es adorno: sin correo el candidato entra al tablero y
 * avanza, pero no se puede contratar, porque la cuenta del sistema se crea con
 * él. Más vale decirlo aquí que al final del proceso.
 *
 * Vive dentro de una pantalla, no de un modal: es la regla de la auditoría —si
 * es una pantalla, es una ruta—, y además el formulario ya no cabía cómodo en
 * una caja flotante.
 */
export const AltaManualDeCandidato = ({
  vacantes,
  vacanteInicial,
  onCancelar,
  onRegistrado,
}: Props) => {
  const [campos, setCampos] = useState<CamposDelAlta>({
    ...VACIO,
    jobRequestId: vacanteInicial ? String(vacanteInicial) : '',
  })
  // El back guarda un solo campo de nombre. Aquí se captura en dos nada más
  // porque así se lee mejor, y se vuelven a unir al validar y al enviar.
  const [nombre, setNombre] = useState('')
  const [apellido, setApellido] = useState('')
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [errorDelServidor, setErrorDelServidor] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [subiendoCv, setSubiendoCv] = useState(false)
  const [nombreDelCv, setNombreDelCv] = useState('')
  const [errorDelCv, setErrorDelCv] = useState<string | null>(null)

  /**
   * El CV sube a S3 en cuanto se elige y lo que se guarda es su URL, igual que
   * en el formulario publico. Subirlo al enviar dejaria a la persona esperando
   * dos veces y, si fallara, con el formulario lleno y sin saber por que.
   */
  const subirCv = async (evento: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = evento.target.files?.[0]
    if (!archivo) return

    const revision = s3Service.validateFile(archivo)
    if (!revision.valid) {
      setErrorDelCv(revision.error ?? 'El archivo no es válido')
      return
    }

    setErrorDelCv(null)
    setSubiendoCv(true)
    setNombreDelCv(archivo.name)
    try {
      const subida = await s3Service.uploadFile(archivo, 'cvs')
      if (subida.success && subida.url) {
        cambia('cvUrl')(subida.url)
      } else {
        setErrorDelCv(subida.message || 'No se pudo subir el archivo')
        setNombreDelCv('')
      }
    } catch {
      setErrorDelCv('No se pudo subir el archivo. Intenta de nuevo.')
      setNombreDelCv('')
    } finally {
      setSubiendoCv(false)
    }
  }

  const [origenes, setOrigenes] = useState<OrigenDeCandidato[]>([])

  const nombreCompleto = `${nombre} ${apellido}`.trim().replace(/\s+/g, ' ')
  const camposConNombre: CamposDelAlta = { ...campos, fullName: nombreCompleto }

  /**
   * Desde dentro se ofrecen todos los activos, publicos e internos: la
   * visibilidad recorta lo que ve el candidato, no lo que captura RH (RH-06).
   *
   * Si la carga falla el campo se queda vacio y el alta sigue: el origen es
   * opcional y no vale la pena frenar un registro por el catalogo.
   */
  useEffect(() => {
    let vigente = true
    origenesDeCandidato
      .todos()
      .then((lista) => vigente && setOrigenes(lista))
      .catch(() => undefined)
    return () => {
      vigente = false
    }
  }, [])

  const pideRecomendador = pideQuienRecomendo(origenes, Number(campos.sourceId))

  /** Quita el error de un campo en cuanto la persona lo corrige. */
  const limpiaError = (campo: string) =>
    setErrores((previos) => {
      if (!previos[campo]) return previos
      const { [campo]: _, ...resto } = previos
      return resto
    })

  const cambia = (campo: keyof CamposDelAlta) => (valor: string) => {
    setCampos((previos) => ({ ...previos, [campo]: valor }))
    limpiaError(campo)
  }

  const cambiaNombre = (valor: string) => {
    setNombre(valor)
    limpiaError('fullName')
  }

  const cambiaApellido = (valor: string) => {
    setApellido(valor)
    limpiaError('fullName')
  }

  const enviar = async (evento: React.FormEvent) => {
    evento.preventDefault()
    setErrorDelServidor('')

    const encontrados = erroresDelAlta(camposConNombre, origenes)
    if (Object.keys(encontrados).length > 0) {
      setErrores(encontrados)
      return
    }

    setGuardando(true)
    try {
      await apiService.registrarCandidatoManual({
        jobRequestId: Number(campos.jobRequestId),
        status: campos.status,
        fullName: nombreCompleto,
        age: Number(campos.age),
        profesion: campos.profesion.trim(),
        phone: campos.phone.trim(),
        email: campos.email,
        sourceId: campos.sourceId ? Number(campos.sourceId) : undefined,
        referredByText: pideRecomendador
          ? campos.referredByText.trim()
          : undefined,
        // Lo que no se capturo no viaja: el DTO corre con forbidNonWhitelisted
        // y una cadena vacia se guardaria como dato.
        experienceYears: campos.experienceYears.trim()
          ? Number(campos.experienceYears)
          : undefined,
        experienceDescription: campos.experienceDescription.trim() || undefined,
        certificaciones: campos.certificaciones.trim() || undefined,
        residenceCity: campos.residenceCity.trim() || undefined,
        salaryExpectation: campos.salaryExpectation.trim()
          ? Number(campos.salaryExpectation)
          : undefined,
        cvUrl: campos.cvUrl || undefined,
        // Solo viaja en Seleccionado: en otra etapa no se pide ni se guarda.
        evaluationUrl:
          campos.status === 'seleccionado' ? campos.evaluationUrl || undefined : undefined,
      })
      onRegistrado()
    } catch (error) {
      // El duplicado por teléfono y la vacante no aprobada llegan por aquí, y
      // son justo los que la persona necesita leer para saber qué hacer.
      setErrorDelServidor(mensajeDeError(error))
    } finally {
      setGuardando(false)
    }
  }

  const etiquetaDeVacante = (v: VacanteMinima) =>
    [v.folio, v.puestoSolicitar].filter(Boolean).join(' · ') || `Vacante ${v.id}`

  const clasesDeCampo = (hayError: boolean) =>
    `w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 ${
      hayError ? 'ring-2 ring-red-400' : 'focus:ring-[#477FFA]'
    }`

  const campoTexto = (
    campo: keyof CamposDelAlta,
    etiqueta: string,
    opciones: { obligatorio?: boolean; tipo?: string; ayuda?: string; placeholder?: string } = {},
  ) => (
    <div>
      <Etiqueta htmlFor={`alta-${campo}`} obligatorio={opciones.obligatorio}>
        {etiqueta}
      </Etiqueta>
      <input
        id={`alta-${campo}`}
        type={opciones.tipo ?? 'text'}
        value={campos[campo]}
        placeholder={opciones.placeholder}
        onChange={(e) => cambia(campo)(e.target.value)}
        aria-invalid={Boolean(errores[campo])}
        aria-describedby={errores[campo] ? `alta-${campo}-error` : undefined}
        className={clasesDeCampo(Boolean(errores[campo]))}
      />
      {errores[campo] ? (
        <p id={`alta-${campo}-error`} className="text-xs text-red-600 mt-1">
          {errores[campo]}
        </p>
      ) : opciones.ayuda ? (
        <p className="text-xs text-gray-500 mt-1">{opciones.ayuda}</p>
      ) : null}
    </div>
  )

  return (
    <form onSubmit={enviar} noValidate className="space-y-6">
      <Seccion titulo="Información del candidato">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Etiqueta htmlFor="alta-nombre" obligatorio>
              Nombre
            </Etiqueta>
            <input
              id="alta-nombre"
              value={nombre}
              placeholder="Mariana"
              onChange={(e) => cambiaNombre(e.target.value)}
              aria-invalid={Boolean(errores.fullName)}
              className={clasesDeCampo(Boolean(errores.fullName))}
            />
          </div>
          <div>
            <Etiqueta htmlFor="alta-apellido" obligatorio>
              Apellido
            </Etiqueta>
            <input
              id="alta-apellido"
              value={apellido}
              placeholder="Cortés Vega"
              onChange={(e) => cambiaApellido(e.target.value)}
              aria-invalid={Boolean(errores.fullName)}
              className={clasesDeCampo(Boolean(errores.fullName))}
            />
          </div>
        </div>
        {errores.fullName && (
          <p className="text-xs text-red-600 -mt-2">{errores.fullName}</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {campoTexto('age', 'Edad', { obligatorio: true, tipo: 'number', placeholder: '28' })}
          {campoTexto('profesion', 'Profesión', {
            obligatorio: true,
            placeholder: 'Ingeniera Industrial',
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {campoTexto('phone', 'Teléfono', {
            obligatorio: true,
            placeholder: '55 1234 5678',
          })}
          {campoTexto('email', 'Correo', {
            // Dice la consecuencia y el motivo: sin correo no hay cuenta, y
            // sin cuenta no hay contratación. El texto anterior solo decía que
            // «hacía falta», sin aclarar para qué ni qué pasa si no se pone.
            ayuda: 'Sin correo no se podrá contratar: su cuenta del sistema se crea con él',
            placeholder: 'mariana@correo.com',
          })}
        </div>

        {/*
          Origen declarado (RH-06). Solo aparece si el catalogo trajo algo:
          un desplegable vacio no ayuda a nadie y ocupa lugar.
        */}
        {origenes.length > 0 && (
          <div>
            <Etiqueta htmlFor="alta-origen">Origen</Etiqueta>
            <SelectBuscable
              variante="formulario"
              id="alta-origen"
              value={campos.sourceId}
              onChange={cambia('sourceId')}
              placeholder="Sin especificar"
              placeholderDeBusqueda="Buscar origen..."
              opciones={[
                { value: '', label: 'Sin especificar' },
                ...origenes.map((origen) => ({
                  value: String(origen.id),
                  label: origen.name,
                })),
              ]}
            />
          </div>
        )}

        {pideRecomendador &&
          campoTexto('referredByText', 'Quién lo recomendó', {
            obligatorio: true,
            placeholder: 'Nombre del colaborador',
            ayuda: 'Después se puede vincular con el directorio desde la ficha',
          })}
      </Seccion>

      {/*
        Lo mismo que captura el formulario publico, aqui opcional (RH-05).

        Antes estos datos solo entraban si el candidato se postulaba solo, asi
        que una alta manual nacia sin experiencia, sin ciudad y sin sueldo —y
        asi se veia despues en la ficha y en la vista de Direccion, con guiones
        donde deberia haber informacion.
      */}
      <Seccion titulo="Perfil y expectativas">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {campoTexto('experienceYears', 'Años de experiencia', {
            tipo: 'number',
            placeholder: '5',
          })}
          {campoTexto('salaryExpectation', 'Sueldo deseado', {
            tipo: 'number',
            placeholder: '22000.00',
            ayuda: 'Mensual bruto, si lo mencionó',
          })}
          <SelectorDeResidencia
            valor={campos.residenceCity}
            onChange={cambia('residenceCity')}
            idBase="alta-residencia"
            etiqueta={(htmlFor, texto) => <Etiqueta htmlFor={htmlFor}>{texto}</Etiqueta>}
            claseInput={clasesDeCampo(Boolean(errores.residenceCity))}
            error={errores.residenceCity}
          />
          {campoTexto('certificaciones', 'Certificaciones', {
            placeholder: 'NOM-009, trabajos en altura',
          })}
        </div>

        <div>
          <Etiqueta htmlFor="alta-experiencia">Resumen de la experiencia</Etiqueta>
          <textarea
            id="alta-experiencia"
            rows={3}
            value={campos.experienceDescription}
            onChange={(e) => cambia('experienceDescription')(e.target.value)}
            placeholder="Dónde trabajó, qué hacía, qué tanto le tocó de lo que pide la vacante"
            className={clasesDeCampo(false)}
          />
        </div>

        <div>
          <Etiqueta htmlFor="alta-cv">CV</Etiqueta>
          <div className="flex items-center gap-3 flex-wrap">
            <label
              htmlFor="alta-cv"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                subiendoCv
                  ? 'bg-gray-100 text-gray-400 cursor-default'
                  : 'bg-[#EEF0FB] text-[#3D4B8F] hover:bg-[#E2E6F7] cursor-pointer'
              }`}
            >
              <Paperclip className="w-4 h-4" />
              {subiendoCv ? 'Subiendo…' : campos.cvUrl ? 'Cambiar archivo' : 'Seleccionar archivo'}
            </label>
            <input
              id="alta-cv"
              type="file"
              accept=".pdf,.doc,.docx"
              disabled={subiendoCv}
              onChange={(e) => void subirCv(e)}
              className="hidden"
            />
            <span className="text-sm text-gray-500">
              {nombreDelCv || 'Sin archivo seleccionado'}
            </span>
          </div>
          {errorDelCv ? (
            <p className="text-xs text-red-600 mt-1">{errorDelCv}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">
              PDF, DOC o DOCX. Opcional, se puede agregar después desde la ficha.
            </p>
          )}
        </div>
      </Seccion>

      <Seccion titulo="Información de la vacante">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Etiqueta htmlFor="alta-vacante" obligatorio>
              Vacante
            </Etiqueta>
            <SelectBuscable
              variante="formulario"
              id="alta-vacante"
              value={campos.jobRequestId}
              onChange={cambia('jobRequestId')}
              error={Boolean(errores.jobRequestId)}
              placeholder="Seleccionar la vacante"
              placeholderDeBusqueda="Buscar vacante..."
              opciones={vacantes.map((v) => ({
                value: String(v.id),
                label: etiquetaDeVacante(v),
              }))}
            />
            {errores.jobRequestId && (
              <p className="text-xs text-red-600 mt-1">{errores.jobRequestId}</p>
            )}
          </div>

          <div>
            <Etiqueta htmlFor="alta-etapa" obligatorio>
              Etapa destino
            </Etiqueta>
            <SelectBuscable
              variante="formulario"
              id="alta-etapa"
              value={campos.status}
              onChange={cambia('status')}
              canDelete={false}
              placeholderDeBusqueda="Buscar etapa..."
              opciones={ETAPAS_DE_ALTA.map((etapa) => ({
                value: etapa.valor,
                label: etapa.etiqueta,
              }))}
            />
            {errores.status && (
              <p className="text-xs text-red-600 mt-1">{errores.status}</p>
            )}
          </div>
        </div>

        {/* Dar de alta ya seleccionado no pasa por una entrevista de RH, así que
            la evaluación se pide aquí (SRH-309). Puede subirse después. */}
        {campos.status === 'seleccionado' && (
          <div className="mt-5">
            <Etiqueta htmlFor="alta-evaluacion" obligatorio nota="se puede subir después">
              Evaluación
            </Etiqueta>
            <CampoDeEvaluacion
              id="alta-evaluacion"
              url={campos.evaluationUrl}
              onCambio={cambia('evaluationUrl')}
            />
            {!campos.evaluationUrl && (
              <p className="text-xs text-[#795E1B] bg-[#FEF6E2] rounded-lg px-3 py-2 mt-2">
                {AVISO_DE_ALTA_SIN_EVALUACION}
              </p>
            )}
          </div>
        )}
      </Seccion>

      {errorDelServidor && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5">
          <p className="text-sm text-red-700">{errorDelServidor}</p>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancelar}
          disabled={guardando}
          className={estiloDeBoton('secundario')}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={guardando}
          className={estiloDeBoton('primario')}
        >
          {guardando ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  )
}
